// CustomImageUploader.jsx - Upload custom images as SCADA components
import React, { useRef, useState } from 'react';
import { X, Upload, Image, Plus, Trash2 } from 'lucide-react';
import { compressImage } from '../../../utils/imageCompressor';

export default function CustomImageUploader({
  customComponents = [],
  onAddComponent,
  onRemoveComponent,
  onSelectForPlacement,
  darkMode = false
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [componentName, setComponentName] = useState('');
  const [componentWidth, setComponentWidth] = useState(64);
  const [componentHeight, setComponentHeight] = useState(64);
  const fileInputRef = useRef(null);

  const bgColor = darkMode ? '#1e293b' : '#ffffff';
  const textColor = darkMode ? '#e5e7eb' : '#111827';
  const borderColor = darkMode ? '#334155' : '#e5e7eb';
  const inputBg = darkMode ? '#0f172a' : '#f8fafc';
  const accentColor = darkMode ? '#3b82f6' : '#2563eb';

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
      alert('Please upload PNG, JPG, SVG, or WebP images.');
      return;
    }

    // Validate file size (2MB limit for components)
    if (file.size > 2 * 1024 * 1024) {
      alert('Component image must be smaller than 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const compressed = await compressImage(event.target.result, { maxDimension: 200 });
      setUploadedImage(compressed);
      setComponentName(file.name.replace(/\.[^/.]+$/, ''));

      // Get image dimensions
      const img = new window.Image();
      img.onload = () => {
        // Scale to max 100px while preserving aspect ratio
        const maxSize = 100;
        let w = img.width;
        let h = img.height;
        if (w > maxSize || h > maxSize) {
          const ratio = Math.min(maxSize / w, maxSize / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        setComponentWidth(w);
        setComponentHeight(h);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAdd = () => {
    if (!uploadedImage || !componentName.trim()) {
      alert('Please upload an image and provide a name.');
      return;
    }

    onAddComponent({
      id: `custom_${Date.now()}`,
      name: componentName.trim(),
      imageData: uploadedImage,
      width: componentWidth,
      height: componentHeight,
      type: 'customImage'
    });

    // Reset form
    setUploadedImage(null);
    setComponentName('');
    setComponentWidth(64);
    setComponentHeight(64);
    setIsModalOpen(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: `1px solid ${borderColor}`,
    backgroundColor: inputBg,
    color: textColor,
    fontSize: '14px',
    outline: 'none'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: darkMode ? '#94a3b8' : '#64748b',
    marginBottom: '4px'
  };

  return (
    <div>
      {/* Custom Components List */}
      <div style={{
        padding: '12px',
        backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
        borderRadius: '8px',
        marginBottom: '8px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Image size={16} color={accentColor} />
            <span style={{ fontSize: '13px', fontWeight: '500', color: textColor }}>
              Custom Components
            </span>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: '4px 8px',
              border: `1px solid ${borderColor}`,
              borderRadius: '4px',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '11px',
              color: textColor
            }}
          >
            <Plus size={12} />
            Add
          </button>
        </div>

        {customComponents.length === 0 ? (
          <div style={{
            padding: '16px',
            textAlign: 'center',
            color: darkMode ? '#64748b' : '#94a3b8',
            fontSize: '12px'
          }}>
            No custom components yet
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px'
          }}>
            {customComponents.map(comp => (
              <div
                key={comp.id}
                style={{
                  position: 'relative',
                  padding: '8px',
                  borderRadius: '6px',
                  border: `1px solid ${borderColor}`,
                  backgroundColor: darkMode ? '#1e293b' : '#ffffff',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                title="Click to place on canvas, or drag"
                onClick={() => onSelectForPlacement && onSelectForPlacement(comp)}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('customComponent', JSON.stringify(comp));
                }}
              >
                <img
                  src={comp.imageData}
                  alt={comp.name}
                  style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'contain'
                  }}
                />
                <div style={{
                  fontSize: '9px',
                  color: textColor,
                  marginTop: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {comp.name}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveComponent(comp.id);
                  }}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '3px',
                    border: 'none',
                    backgroundColor: 'rgba(239, 68, 68, 0.8)',
                    color: '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0
                  }}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: bgColor,
            borderRadius: '12px',
            width: '360px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 20px',
              borderBottom: `1px solid ${borderColor}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Upload size={20} color={accentColor} />
                <h3 style={{ margin: 0, color: textColor, fontSize: '16px', fontWeight: '600' }}>
                  Add Custom Component
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setUploadedImage(null);
                  setComponentName('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color={darkMode ? '#94a3b8' : '#64748b'} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Upload Area */}
              {!uploadedImage ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${borderColor}`,
                    borderRadius: '8px',
                    padding: '24px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: inputBg
                  }}
                >
                  <Upload size={32} color={darkMode ? '#64748b' : '#94a3b8'} style={{ marginBottom: '8px' }} />
                  <p style={{ margin: 0, fontSize: '13px', color: textColor }}>
                    Click to upload image
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: darkMode ? '#64748b' : '#94a3b8' }}>
                    PNG, JPG, SVG (max 2MB)
                  </p>
                </div>
              ) : (
                <div style={{
                  position: 'relative',
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: inputBg,
                  textAlign: 'center'
                }}>
                  <img
                    src={uploadedImage}
                    alt="Preview"
                    style={{
                      maxWidth: '100px',
                      maxHeight: '100px',
                      objectFit: 'contain'
                    }}
                  />
                  <button
                    onClick={() => setUploadedImage(null)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: 'rgba(239, 68, 68, 0.9)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}

              {/* Component Name */}
              <div>
                <label style={labelStyle}>Component Name</label>
                <input
                  type="text"
                  value={componentName}
                  onChange={(e) => setComponentName(e.target.value)}
                  placeholder="e.g., Tank, Motor, Sensor"
                  style={inputStyle}
                />
              </div>

              {/* Dimensions */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Width (px)</label>
                  <input
                    type="number"
                    value={componentWidth}
                    onChange={(e) => setComponentWidth(parseInt(e.target.value) || 64)}
                    min={20}
                    max={200}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Height (px)</label>
                  <input
                    type="number"
                    value={componentHeight}
                    onChange={(e) => setComponentHeight(parseInt(e.target.value) || 64)}
                    min={20}
                    max={200}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '16px 20px',
              borderTop: `1px solid ${borderColor}`
            }}>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setUploadedImage(null);
                  setComponentName('');
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: `1px solid ${borderColor}`,
                  backgroundColor: 'transparent',
                  color: textColor,
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={!uploadedImage || !componentName.trim()}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: (!uploadedImage || !componentName.trim())
                    ? (darkMode ? '#334155' : '#cbd5e1')
                    : accentColor,
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (!uploadedImage || !componentName.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                Add Component
              </button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      )}
    </div>
  );
}
